import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiHeart } from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/main.scss";

function Main() {
  const [featuredTab, setFeaturedTab] = useState("new");
  const { toggleWishlist, isWishlisted } = useContext(CartContext);

  const featuredProducts = useMemo(() => {
    const tag = featuredTab === "new" ? "new" : "best-seller";
    const tagged = productData.filter((product) => product.tags?.includes(tag));
    return (tagged.length ? tagged : productData).slice(0, 5);
  }, [featuredTab]);

  const categoryNames = [
    "Planners",
    "Planner Inserts",
    "Notebooks",
    "Writing Tools",
    "Storage & Desk",
    "Gift Sets",
  ];

  const categories = categoryNames.map((category) => ({
    name: category,
    product: productData.find((item) => item.category === category),
  }));

  const money = (value) =>
    new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
      style: "currency",
      currency: siteConfig.currency?.code || "USD",
    }).format(Number(value));

  const productCard = (product) => (
    <article className="product-card" key={product.id}>
      <div className="product-card__media">
        <Link to={`/shopDetails/${product.id}`} aria-label={`View ${product.name}`}>
          <img src={product.image} alt={product.name} loading="lazy" />
        </Link>
        {product.tags?.includes("new") && <span className="product-badge">NEW</span>}
        <button
          type="button"
          className={`product-card__wish ${
            isWishlisted(product.id) ? "is-active" : ""
          }`}
          onClick={() => toggleWishlist(product.id)}
          aria-label={
            isWishlisted(product.id)
              ? `Remove ${product.name} from wishlist`
              : `Add ${product.name} to wishlist`
          }
        >
          <FiHeart />
        </button>
        <Link className="product-card__quick" to={`/shopDetails/${product.id}`}>
          CHOOSE OPTIONS
        </Link>
      </div>
      <div className="product-card__details">
        <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
        <span>{money(product.price)}</span>
        <small>{product.colors?.length || 1} options available</small>
      </div>
    </article>
  );

  return (
    <div className="home-page">
      <main id="main-content">
        <section className="home-hero">
          <img
            src="/img/generated/hero-planning.jpg"
            alt="A considered leather planner and writing tools in warm morning light"
          />
          <div className="home-hero__shade" />
          <div className="home-hero__content">
            <p className="eyebrow">THE ART OF PAPER PLANNING</p>
            <h1>PLAN WITH INTENTION.<br />LIVE WITH CLARITY.</h1>
            <p>
              Refined planning tools designed to bring focus, flexibility, and
              a quieter rhythm to every day.
            </p>
            <div>
              <Link className="button button--light" to="/shop?tag=planner-system">
                START PLANNING
              </Link>
              <Link className="home-hero__secondary" to="/shop?category=Gift+Sets">
                DISCOVER THE CLARITY EDIT <FiArrowRight />
              </Link>
            </div>
          </div>
          <span className="home-hero__index">01 / 03</span>
        </section>

        <section className="featured-collections page-width">
          <div className="section-heading">
            <div>
              <p className="eyebrow">A PLACE TO BEGIN</p>
              <h2>FEATURED COLLECTIONS</h2>
            </div>
            <div className="section-arrows" aria-hidden="true">
              <FiArrowLeft />
              <FiArrowRight />
            </div>
          </div>
          <div className="collection-rail">
            {categories.map(({ name, product }, index) => (
              <Link key={name} to={`/shop?category=${encodeURIComponent(name)}`}>
                <div>
                  {product ? (
                    <img src={product.image} alt="" loading="lazy" />
                  ) : (
                    <span>{String(index + 1).padStart(2, "0")}</span>
                  )}
                </div>
                <h3>{name.toUpperCase()}</h3>
              </Link>
            ))}
          </div>
        </section>

        <section className="editorial-feature editorial-feature--light">
          <div className="editorial-feature__media">
            <img
              src="/img/generated/editorial-collection.jpg"
              alt="A modular collection of neutral leather planners and inserts"
            />
          </div>
          <div className="editorial-feature__copy">
            <p className="eyebrow">YOUR MODULAR PLANNING FOUNDATION</p>
            <h2>A SYSTEM THAT<br />GROWS WITH YOU.</h2>
            <p>
              Start with structure, then shape it around the way you think.
              Flexible covers, considered inserts, and tactile details make
              every system distinctly yours.
            </p>
            <Link className="text-link" to="/shop?tag=planner-system">
              SHOP PLANNER SYSTEMS
            </Link>
          </div>
        </section>

        <section className="home-products page-width">
          <div className="section-heading section-heading--products">
            <div>
              <p className="eyebrow">CURATED FOR THE DESK</p>
              <h2>FEATURED PRODUCTS</h2>
            </div>
            <div className="product-tabs" role="tablist" aria-label="Featured products">
              <button
                type="button"
                className={featuredTab === "new" ? "is-active" : ""}
                onClick={() => setFeaturedTab("new")}
                role="tab"
                aria-selected={featuredTab === "new"}
              >
                NEW ARRIVALS
              </button>
              <button
                type="button"
                className={featuredTab === "best" ? "is-active" : ""}
                onClick={() => setFeaturedTab("best")}
                role="tab"
                aria-selected={featuredTab === "best"}
              >
                BEST SELLERS
              </button>
            </div>
            <Link
              className="text-link section-heading__link"
              to={featuredTab === "new" ? "/shop?collection=new" : "/shop?tag=best-seller"}
            >
              SHOP THE COLLECTION
            </Link>
          </div>
          <div className="product-card-grid product-card-grid--home">
            {featuredProducts.map(productCard)}
          </div>
        </section>

        <section className="editorial-feature editorial-feature--reverse editorial-feature--dark">
          <div className="editorial-feature__media">
            <img
              src="/img/generated/clarity-edit-main.jpg"
              alt="An elevated stationery box arranged on charcoal stone"
            />
          </div>
          <div className="editorial-feature__copy">
            <p className="eyebrow">A COMPLETE DESK RITUAL</p>
            <h2>THE CLARITY<br />EDIT.</h2>
            <p>
              A considered gift set of paper goods and desk essentials, composed
              as a calm, coordinated invitation to begin.
            </p>
            <Link className="button button--light" to="/shop?category=Gift+Sets">
              SEE WHAT'S INSIDE
            </Link>
          </div>
        </section>

        <section className="recognized-strip">
          <p className="eyebrow">THE PRINCIPLES BEHIND EVERY CEOWANDMILL PIECE</p>
          <div>
            <span>MODULAR DESIGN</span>
            <span>TACTILE PAPER</span>
            <span>QUIET UTILITY</span>
            <span>DAILY CLARITY</span>
            <span>ENDURING MATERIALS</span>
          </div>
        </section>

        <section className="story-banner">
          <img
            src="/img/generated/story-studio.jpg"
            alt="Hands thoughtfully assembling a ring planner at a studio table"
          />
          <div className="story-banner__copy">
            <p className="eyebrow">WHERE PASSION MEETS PAPER</p>
            <h2>AN ORGANIZED LIFE IS THE FOUNDATION OF A FULFILLED ONE.</h2>
            <p>
              We create tools that turn planning into a tactile pause — a place
              to see what matters and make room for it.
            </p>
            <Link className="button button--light" to="/about">
              DISCOVER OUR STORY
            </Link>
          </div>
        </section>

        <section className="journal-section page-width">
          <div className="section-heading">
            <div>
              <p className="eyebrow">FROM THE JOURNAL</p>
              <h2>IDEAS FOR A MORE INTENTIONAL DAY</h2>
            </div>
            <Link className="text-link" to="/about">DISCOVER MORE IDEAS</Link>
          </div>
          <div className="journal-grid">
            <article>
              <img src="/img/generated/journal-weekly-reset.png" alt="Open weekly planner in warm morning light" />
              <p className="eyebrow">THE PRACTICE · 6 MIN READ</p>
              <h3>A gentler weekly reset for a clearer Monday</h3>
              <Link className="text-link" to="/about">READ THE NOTE</Link>
            </article>
            <article>
              <img src="/img/generated/journal-modular-guide.png" alt="Neutral modular planner and refill system" />
              <p className="eyebrow">THE GUIDE · 8 MIN READ</p>
              <h3>How to build a planning system around your life</h3>
              <Link className="text-link" to="/about">READ THE NOTE</Link>
            </article>
            <article>
              <img src="/img/generated/journal-desk-edit.png" alt="Quiet desk objects arranged for focused work" />
              <p className="eyebrow">THE DESK EDIT · 4 MIN READ</p>
              <h3>Five quiet objects that make focus feel natural</h3>
              <Link className="text-link" to="/about">READ THE NOTE</Link>
            </article>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Main;
